import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Alert, Button, Card, Empty, Form, Input, Pagination, Select, Spin, Switch, Table, Tag, Typography, type TableProps } from 'antd'
import { ReloadOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons'
import type { AccountProjection, AssignmentUpdate, InstanceDirectory } from '../contracts'
import type { useAccounts } from '../hooks/useAccounts'
import type { ApiError } from '@/services/http/errors'

const STATUS_OPTIONS = { true: 'active', false: 'disabled' } as const
const STATUS_TAGS = { active: { label: '启用', color: 'success', checked: true }, disabled: { label: '停用', color: 'default', checked: false } }
const TABLE_SCROLL = { x: 700 }
const EXPAND_ICON = <RightOutlined />
const rowKey = (account: AccountProjection) => JSON.stringify([account.source, account.user_id])

// Keep Ant Design's expansion callback and keyboard button behavior; only replace its visual.
const AccountExpansion = ({ expanded, record, onExpand }: Parameters<NonNullable<NonNullable<TableProps<AccountProjection>['expandable']>['expandIcon']>>[0]) => {
  const click = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    onExpand(record, event)
  }, [onExpand, record])
  return <Button type="text" size="small" aria-label={expanded ? '收起账号' : '展开账号'} aria-expanded={expanded}
    icon={EXPAND_ICON} onClick={click} className="instance-administration__expand" />
}

const AssignmentEditor = ({ account, directory, saving, error, saved, onSave, onReload }: {
  account: AccountProjection; directory: InstanceDirectory; saving: boolean; error: Error | null; saved: boolean
  onSave: (command: AssignmentUpdate) => Promise<AccountProjection>; onReload: () => Promise<void>
}) => {
  const [instanceId, setInstanceId] = useState(account.instance_id ?? '')
  const [status, setStatus] = useState(account.status)
  // A draft keeps its read revision; background refresh must not authorize overwriting newer data.
  const [expectedRevision, setExpectedRevision] = useState(account.revision)
  const live = useRef(true)
  useEffect(() => { live.current = true; return () => { live.current = false } }, [])
  const instanceChanged = useCallback((value: string) => setInstanceId(value), [])
  const statusChanged = useCallback((value: boolean) => setStatus(STATUS_OPTIONS[`${value}` as const]), [])
  const submit = useCallback(() => {
    void onSave({ source: account.source, user_id: account.user_id, instance_id: instanceId || null,
      status, expected_revision: expectedRevision }).then((accepted) => {
      if (live.current) setExpectedRevision(accepted.revision)
    }).catch(() => undefined)
  }, [account, expectedRevision, instanceId, onSave, status])
  const reload = useCallback(() => { void onReload() }, [onReload])
  const options = useMemo(() => [{ value: '', label: '使用默认分配', selectedLabel: '使用默认分配', 'aria-label': '使用默认分配' },
    ...directory.instances.map((item) => ({ value: item.instance_id, disabled: !item.enabled,
      selectedLabel: (item.display_name ?? '未配置名称') + (item.enabled ? '' : '（已停用）'),
      'aria-label': (item.display_name ?? '未配置名称') + ' ' + item.instance_id,
      label: <span className="instance-administration__instance-label">
        <span>{item.display_name ?? '未配置名称'}{item.enabled ? '' : '（已停用）'}</span>
        <span className="instance-administration__identifier">{item.instance_id}</span>
      </span> }))], [directory.instances])
  const savedCurrent = saved && instanceId === (account.instance_id ?? '') && status === account.status
  return <div className="instance-administration__editor">
    <Form layout="vertical" onFinish={submit} disabled={saving}>
      <Form.Item label="智能体角色" htmlFor="instance-assignment-target">
        <Select id="instance-assignment-target" aria-label="智能体角色" value={instanceId} options={options}
          optionLabelProp="selectedLabel" onChange={instanceChanged} className="instance-administration__instance-select" />
      </Form.Item>
      <Form.Item label="分配状态" htmlFor="instance-assignment-status">
        <Switch id="instance-assignment-status" aria-label="分配状态" checked={STATUS_TAGS[status].checked}
          checkedChildren="启用" unCheckedChildren="停用" onChange={statusChanged} />
      </Form.Item>
      <div className="instance-administration__form-actions">
        <Button type="primary" htmlType="submit" loading={saving} disabled={saving || (error as ApiError | null)?.status === 409}>保存分配</Button>
        <Button type="text" onClick={reload} disabled={saving} icon={<ReloadOutlined />}>重新读取</Button>
      </div>
      {error ? <Alert type="error" showIcon title={error.message} role="alert" /> : null}
      {savedCurrent ? <Alert type="success" showIcon title="已保存" role="status" /> : null}
    </Form>
  </div>
}

export const AccountPanel = ({ model }: { model: ReturnType<typeof useAccounts> }) => {
  const { select, search, setPage, reload } = model
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [editorResets, setEditorResets] = useState(new Map<string, number>())
  const choose = useCallback((expanded: boolean, account: AccountProjection) => {
    setExpandedRowKeys(expanded ? [rowKey(account)] : [])
    if (expanded) select({ source: account.source, user_id: account.user_id })
  }, [select])
  const searchChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => search(event.target.value), [search])
  const pageChanged = useCallback((value: number) => setPage(value), [setPage])
  const retry = useCallback(() => { void reload() }, [reload])
  const columns = useMemo<TableProps<AccountProjection>['columns']>(() => [
    { title: '账号', dataIndex: 'user_id', width: 240, ellipsis: true,
      render: (value: string) => <Typography.Text strong>{value}</Typography.Text> },
    { title: '来源', dataIndex: 'source', width: 140, ellipsis: true, render: (value: string) => <Typography.Text type="secondary">{value || '未记录来源'}</Typography.Text> },
    { title: '当前智能体角色', key: 'instance', render: (_value: unknown, item) => <div className="instance-administration__instance-label">
      <Typography.Text>{item.effective_instance_display_name ?? '未配置名称'}</Typography.Text>
      <Typography.Text type="secondary" className="instance-administration__identifier">{item.effective_instance_id}</Typography.Text>
    </div> },
    { title: '分配状态', dataIndex: 'status', width: 104, render: (value: AccountProjection['status']) => <Tag color={STATUS_TAGS[value].color}>{STATUS_TAGS[value].label}</Tag> },
  ], [])
  const locale = useMemo(() => ({ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无账号" />,
    expand: '展开账号', collapse: '收起账号' }), [])
  const readError = model.directory.error || model.accounts.error
  const account = model.preview.data
  const directory = model.directory.data
  // Reset only the reloaded row; a late reload must not remount another account's draft.
  const reloadEditor = useCallback(async () => {
    if (!account) return
    const key = rowKey(account)
    await reload()
    setEditorResets((previous) => new Map(previous).set(key, (previous.get(key) ?? 0) + 1))
  }, [account, reload])
  const retryAction = useMemo(() => <Button size="small" onClick={retry}>重新读取</Button>, [retry])
  const renderEditor = useCallback((item: AccountProjection) => {
    if (model.preview.error) return <Alert type="error" showIcon title={model.preview.error.message} action={retryAction} role="alert" />
    if (!account || !directory || rowKey(account) !== rowKey(item)) {
      return <Spin description="正在读取分配"><div className="instance-administration__pending" /></Spin>
    }
    return <AssignmentEditor key={rowKey(account) + ':' + (editorResets.get(rowKey(account)) ?? 0)}
      account={account} directory={directory} saving={model.saving || !!readError || model.preview.isFetching}
      error={model.saveError} saved={model.saved} onSave={model.save} onReload={reloadEditor} />
  }, [account, directory, editorResets, model.preview.error, model.preview.isFetching, model.save,
    model.saveError, model.saved, model.saving, readError, reloadEditor, retryAction])
  const expandIcon = useCallback<NonNullable<NonNullable<TableProps<AccountProjection>['expandable']>['expandIcon']>>((props) => <AccountExpansion {...props} />, [])
  const expandable = useMemo<NonNullable<TableProps<AccountProjection>['expandable']>>(() => ({
    expandedRowKeys, onExpand: choose, expandedRowRender: renderEditor, expandRowByClick: true, expandIcon, columnWidth: 40,
  }), [choose, expandIcon, expandedRowKeys, renderEditor])
  return <section className="instance-administration__content">
    {readError ? <Alert className="instance-administration__read-error" type="error" showIcon title={readError.message} action={retryAction} /> : null}
      <Card title="账号列表" className="instance-administration__directory">
        <Input aria-label="搜索账号" placeholder="搜索账号" prefix={<SearchOutlined />} value={model.query}
          onChange={searchChanged} allowClear className="instance-administration__search" />
        <Table<AccountProjection> columns={columns} dataSource={model.accounts.data?.accounts} rowKey={rowKey}
          pagination={false} loading={model.accounts.isFetching} scroll={TABLE_SCROLL} locale={locale} expandable={expandable} />
        <Pagination current={model.page} total={model.accounts.data?.total ?? 0} pageSize={20} showSizeChanger={false}
          onChange={pageChanged} disabled={model.accounts.isFetching} className="instance-administration__pagination" />
      </Card>
  </section>
}
